import { useStore } from '@/hooks/useStore';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious
} from '@/components/common/Carousel';
import { isPathEk } from '@/utils/routes';
import { flowResult } from 'mobx';
import { COMMON_TYPE, ROLE_USER_CODE } from '@/types/enums';
import BikeDetailTabs from './tabs';
import ImagesBike from './components/images-bike/ImagesBike';
import BikeItem from '@/pages/location-enduser/components/location-detail/components/BikeItem';
import { SubTitleWithBox, SubTitleWithTwoBox } from '@/components/general/title/sub-title/SubTitle';
import AnimateButton from '@/components/general/button-custom/AnimateButton';
import { ekDomain, ROUTES, SEAT_HEIGHT_SELECT } from '@/configs/constants';
import { v4 as uuidv4 } from 'uuid';
import BannerLayout from '@/components/layout/banner-layout';
import { ChevronRight } from 'lucide-react';
import BikeDetailRemarks from './components/BikeDetailRemarks';

export default observer(function BikeBookingDetailPage() {
    // hook
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id, bikeId } = useParams();
    const { pathname } = useLocation();

    const isEk = isPathEk(pathname);
    const type = isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS;

    // store
    const {
        bookingStore: {
            getBikeBookingDetail,
            bikeBookingDetail,
            bikeImages,
            listBikeLocation,
            getAllBikeInLocation,
            setObservable
        },
        locationBikeStore: {
            // keepSearchParams,
            setObservable: setObservableLocationBike
        },
        authStore: { tokenEk, tokenGas, profileGas, profileEk },
        modalStore: { showAlertModal }
    } = useStore();

    // state
    const bikes =
        listBikeLocation?.length > 0 ?
            listBikeLocation?.filter(item => item.id !== bikeId) :
            [];

    // lifecycle
    useEffect(() => {
        onFetch(Number(bikeId), Number(id));

        return () => {
            setObservable('bikeBookingDetail', undefined);
            setObservable('bikeImages', []);
        };
    }, [bikeId, id]);

    // function
    const onFetch = async (bikeId: number, locationId: number) => {
        const res = await flowResult(
            getBikeBookingDetail(bikeId, locationId, type)
        );
        let params: any = {};
        if (res?.bikeCapacity?.id) {
            params.engineSizeId = Number(res?.bikeCapacity?.id);
        }
        params.type = type;
        await flowResult(getAllBikeInLocation(locationId, params));
    };

    const onClickBooking = () => {
        // check role
        if (isEk) {
            if (!profileEk?.roleCode || !tokenEk) {
                // saveCurrentHref();
                navigate(`/${ekDomain}${ROUTES.signin.hrefEK}`, {
                    state: { previousUrl: `${window.location.href}` }
                });
                return;
            }

            if (profileEk?.roleCode !== ROLE_USER_CODE.RENTAL_BIKE_OWNER_EK) {
                showAlertModal({
                    id: 'role-error',
                    type: 'error',
                    content: (
                        <Trans i18nKey='messages.check_role_before_booking_bike' />
                    )
                });
                return;
            }
        } else {
            if (!profileGas?.roleCode || !tokenGas) {
                // saveCurrentHref();
                navigate(`${ROUTES.signin.href}`, {
                    state: { previousUrl: `${window.location.href}` }
                });
                return;
            }

            if (profileGas?.roleCode !== ROLE_USER_CODE.RENTAL_BIKE_OWNER_RD) {
                showAlertModal({
                    id: 'role-error',
                    type: 'error',
                    content: (
                        <Trans i18nKey='messages.check_role_before_booking_bike' />
                    )
                });
                return;
            }
        }

        const sessionToken = uuidv4();

        navigate(
            isEk ?
                `/${ekDomain}/${ROUTES.locationList.href}/${ROUTES.locationDetailEnduser.href}/${id}/${ROUTES.bikeBookingFirst.href}/${bikeId}?uuid=${sessionToken}` :
                `/${ROUTES.locationList.href}/${ROUTES.locationDetailEnduser.href}/${id}/${ROUTES.bikeBookingFirst.href}/${bikeId}?uuid=${sessionToken}`
        );
    };

    const handleClickViewAll = () => {
        const tmpBikeDetail = {
            ...bikeBookingDetail
        };

        setObservableLocationBike('keepSearchParams', {
            engineSizeId: tmpBikeDetail?.bikeCapacity?.id
        });
        setObservable('bikeBookingDetail', undefined);

        navigate(
            isEk ?
                `/${ekDomain}/${ROUTES.locationList.href}/${ROUTES.locationDetailEnduser.href}/${id}#list-bike` :
                `/${ROUTES.locationList.href}/${ROUTES.locationDetailEnduser.href}/${id}#list-bike`
        );
    };

    // 足付き(シート高): chưa chọn thì không hiển thị
    const seatHeightOption = SEAT_HEIGHT_SELECT.find(item => item.value === bikeBookingDetail?.seatHeight);

    return (
        <div className='overflow-x-hidden'>
            <BannerLayout
                title='バイクの詳細'
                subTitle='バイクの詳細'
                url=''
                className='hidden sm:block'
            />

            <div className='wrapper-inner mt-[min(120px,15vw)] under-wrapper'>
                <div className='block sm:hidden pb-[20px]'>
                    <SubTitleWithTwoBox
                        title='バイク詳細'
                        sub='ABOUT BIKE'
                        subClassName='!text-fs-mid12 !leading-[18px]'
                        titleClassName='!text-[24px] break-all'
                    />
                </div>

                <div className='w-full max-w-[800px] mx-auto'>
                    <ImagesBike images={bikeImages} />
                </div>

                <div className={`flex justify-start sm:justify-center items-center gap-[min(20px,2.8vw)] mt-[min(40px,5.2vw)] ${seatHeightOption ? 'mb-[min(24px,3.2vw)]' : 'mb-[min(80px,10.3vw)]'}`}>
                    <span className='flex justify-center items-center text-primaryWeb text-[12px] sm:text-fs-mid14 leading-[1] px-[10px] py-[4px] sm:px-[20px] sm:py-[10px] border border-primaryWeb rounded-[3px] whitespace-nowrap'>
                        バイク名
                    </span>
                    <span className='text-[18px] leading-[calc(24/18)] sm:text-fs-mid24 sm:leading-lh-mid24 font-bold text-[#000] break-all'>
                        {bikeBookingDetail?.bikeModel || '--'}
                    </span>
                </div>
                {seatHeightOption && (
                    <p className='text-left sm:text-center text-fs-mid14 sm:text-fs-mid16 font-bold text-[#000] mb-[min(80px,10.3vw)]'>
                        {`${t('field.seat_height')}：${t(seatHeightOption.name)}`}
                    </p>
                )}
                <BikeDetailRemarks note={bikeBookingDetail?.note} />
                <div className='w-full'>
                    <BikeDetailTabs />
                </div>

                <div className='w-full flex justify-center pt-[40px]'>
                    <AnimateButton
                        contentComp={t('button.make_a_reservation')}
                        parentClassName='h-[67px] w-[420px]'
                        className=''
                        textClassName='text-fs-mid18 leading-normal'
                        onClick={onClickBooking}
                    />
                </div>
            </div>

            {/* All bikes same engine size(in the same location) */}
            <div className='w-full bg-background-gray py-[min(120px,15vw)]'>
                <div className='wrapper-inner flex flex-col gap-[20px]'>
                    <SubTitleWithBox title={t('booking_bike.title.bikes_same_engine_size')} />

                    <div className='px-[10px] md:px-0'>
                        <Carousel>
                            {bikes?.length > 0 && (
                                <CarouselPrevious className='border-none flex absolute top-1/2 left-[15px] xl:-left-[24px] transform -translate-y-1/2 -translate-x-full shadow-md z-10' />
                            )}
                            <CarouselContent className='w-full snap-x snap-mandatory ml-0 gap-[20px]'>
                                {bikes?.length > 0 ?
                                    (
                                        bikes?.map(item => (
                                            <CarouselItem
                                                key={item.id}
                                                className='pl-0 pr-0 basis-[100%] xsm:basis-[70%] point-600:basis-[48%] md:basis-[45%] point-900:basis-[32%]'
                                            >
                                                <BikeItem bike={item} locationId={id} />
                                            </CarouselItem>
                                        ))
                                    ) :
                                    (
                                        <div className='w-full h-40 flex items-center justify-center'>
                                            {t('sentences.no_data')}
                                        </div>
                                    )}
                            </CarouselContent>
                            {bikes?.length > 0 && (
                                <CarouselNext className='border-none flex absolute top-1/2 right-[15px] xl:-right-[24px] transform -translate-y-1/2 translate-x-full shadow-md z-10' />
                            )}
                        </Carousel>
                    </div>

                    {bikes?.length > 0 && (
                        <div onClick={handleClickViewAll} className='flex font-semibold items-center justify-end space-x-5 cursor-pointer'>
                            <div className='cursor-pointer'>
                                一覧を見る
                            </div>
                            <div className='w-6 rounded-full h-6 bg-primaryWeb flex justify-center items-center'>
                                <ChevronRight className='text-[#fff] size-4' />
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
});
